<template>
    <div class="team-page">
        <team-banner :team="team" :selected-league="selectedLeague" :phase-counts="phaseCounts" @setType="setType" @setLeague="setLeague" />
        <div class="cl-page">
            <v-tabs v-model="tab" class="cl-tabs mb-6" background-color="transparent" show-arrows>
                <v-tabs-slider class="custom-teal" />
                <v-tab v-for="(item, index) in items" :key="index">
                    {{ item.name }}
                </v-tab>
            </v-tabs>
            <team-standings v-if="tab === 0" :ranking="ranking" :show-filters="false" :matches="matches" :bracket-scope="bracketScope" :type="type" @show-all="tab = 2" />
            <roster v-else-if="tab === 1" :players="team.players || []" :color="team.color" />
            <schedule-team v-else-if="tab === 2" :matches="matches" :team-id="team.id" />
            <team-stats v-else-if="tab === 3" :players="team.players" :matches-played="team.seasonLoses + team.seasonWins" :type="type" />
        </div>
    </div>
</template>

<script>
    import routeTab from '@/mixins/routeTab'
    import TeamBanner from '@/components/TeamBanner'
    import Roster from '@/components/Roster'
    import ScheduleTeam from '@/components/ScheduleAlternative'
    import TeamStats from '@/components/TeamStats'
    import teamService from '@/services/team'
    import TeamStandings from '@/components/TeamStandings'
    import { foulsOf } from '@/utils/teamStats'
    import matchService from '@/services/match'

    export default {
        components: { TeamBanner, Roster, ScheduleTeam, TeamStats, TeamStandings },
        mixins: [routeTab],
        data() {
            return {
                tab: null,
                items: [
                    {
                        name: 'Κεντρική'
                    },
                    {
                        name: 'Ρόστερ'
                    },
                    {
                        name: 'Αγώνες'
                    },
                    {
                        name: 'Στατιστικά'
                    }
                ],
                team: {},
                ranking: [],
                page: 1,
                size: 1000,
                totalPages: 0,
                matches: [],
                type: 'regular_season',
                matchTypes: ['regular_season', 'playoffs', 'cup'],
                // matches of the team in every phase, for the hint on the phase buttons
                phaseCounts: null,
                seasonId: null,
                selectedLeague: null
            }
        },
        computed: {
            // the bracket of the phase on screen: the cup of the team, or the playoffs of the league chosen
            bracketScope() {
                const seasonId = this.$store.getters.getConfig?.activeSeasonId
                if (!seasonId) return null
                if (this.type === 'cup') {
                    return this.team.cupLeague ? { seasonId, cup: true, cupLeagueId: this.team.cupLeague.id } : null
                }
                if (this.type === 'playoffs') {
                    const league = this.selectedLeague || this.team.league
                    return league ? { seasonId, leagueId: league.id } : null
                }
                return null
            }
        },
        watch: {
            '$route.params.id': {
                handler(newId, oldId) {
                    if (newId !== oldId) {
                        this.loadTeamData(newId)
                    }
                }
            },
            type: {
                handler(newType, oldType) {
                    if (newType !== oldType) {
                        this.loadTeamData()
                    }
                }
            }
        },
        created() {
            this.loadTeamData()
        },
        methods: {
            loadTeamData(teamId = this.$route.params.id) {
                if (!teamId) {
                    this.team = {}
                    this.ranking = []
                    this.matches = []
                    return
                }
                // wait for the team so selectedLeague is resolved before the league-scoped requests
                this.getTeam(teamId).then(() => {
                    this.getRanking(teamId)
                    this.fetchMatchesByTeam(teamId)
                    this.fetchPhaseCounts(teamId)
                })
            },
            getMatchesPlayed() {
                if (this.type === 'cup') {
                    this.team.seasonLoses = 0
                    this.team.seasonWins = 0
                    this.matches.forEach(element => {
                        if (element.team1.id === parseInt(this.$route.params.id) && element.scoreTeam1 > element.scoreTeam2) {
                            this.team.seasonWins += 1
                        } else if (element.team1.id === parseInt(this.$route.params.id) && element.scoreTeam1 < element.scoreTeam2) {
                            this.team.seasonLoses += 1
                        } else if (element.team2.id === parseInt(this.$route.params.id) && element.scoreTeam2 > element.scoreTeam1) {
                            this.team.seasonWins += 1
                        } else if (element.team2.id === parseInt(this.$route.params.id) && element.scoreTeam2 < element.scoreTeam1) {
                            this.team.seasonLoses += 1
                        }
                    })
                }
            },
            setType(option) {
                const nextType = this.matchTypes[option]
                if (this.type === nextType) {
                    return
                }
                this.type = nextType
            },
            setLeague(league) {
                if (!league || (this.selectedLeague && this.selectedLeague.id === league.id)) {
                    return
                }
                this.selectedLeague = league
                const teamId = this.$route.params.id
                this.getTeam(teamId)
                this.getRanking(teamId)
                this.fetchMatchesByTeam(teamId)
                this.fetchPhaseCounts(teamId)
            },
            // one request per phase (the league's for the regular season and the play offs); a failure counts as none
            fetchPhaseCounts(teamId) {
                const leagueId = this.selectedLeague ? this.selectedLeague.id : null
                Promise.all(this.matchTypes.map(phase =>
                    matchService.getMatchesByTeam(teamId, null, phase, phase !== 'cup' ? leagueId : null)
                        .then(resp => (Array.isArray(resp.data) ? resp.data.length : 0))
                        .catch(() => 0)
                )).then(counts => {
                    if (String(teamId) === String(this.$route.params.id)) {
                        this.phaseCounts = counts
                    }
                })
            },
            getTeam(teamId) {
                const leagueId = this.type !== 'cup' && this.selectedLeague ? this.selectedLeague.id : null
                return teamService.getTeam(teamId, this.type, leagueId).then(resp => {
                    this.team = resp.data
                    const leagues = this.team.leagues || []
                    if (!this.selectedLeague || !leagues.some(league => league.id === this.selectedLeague.id)) {
                        this.selectedLeague = this.team.league || leagues[0] || null
                        // first response was not league-scoped; refetch for multi-league teams
                        if (!leagueId && this.type !== 'cup' && this.selectedLeague && leagues.length > 1) {
                            return this.getTeam(teamId)
                        }
                    }
                    this.calculateTeamStats()
                    this.getMatchesPlayed()
                })
            },
            getRanking(teamId) {
                const leagueId = this.type !== 'cup' && this.selectedLeague ? this.selectedLeague.id : null
                teamService.getRankingByTeam(teamId, this.type, leagueId).then(resp => {
                    this.ranking = resp.data
                    // this.ranking.sort(this.compare)
                })
            },
            compare(a, b) {
                if (a.standing < b.standing) {
                    return 1
                }
                if (a.standing > b.standing) {
                    return -1
                }
                if (a.standing === b.standing) {
                    return b.pointsDiff >= a.pointsDiff ? 1 : -1
                }
                return 0
            },
            calculateTeamStats() {
                this.team.players.forEach(player => {
                    player.avg = this.getAverageStats(player)
                    player.cum = this.getCummulativeStats(player)
                })
            },
            getCummulativeStats(player) {
                const stats = {
                    points: 0,
                    rebounds: 0,
                    assists: 0,
                    steals: 0,
                    turnovers: 0,
                    blockedShots: 0,
                    twoPointsMade: 0,
                    twoPointAttempts: 0,
                    threePointsMade: 0,
                    threePointAttempts: 0,
                    freeThrowsMade: 0,
                    freeThrowAttempts: 0,
                    offensiveRebounds: 0,
                    defensiveRebounds: 0,
                    personalFouls: 0,
                    fouls: 0,
                    pir: 0,
                    playTimeInSecs: 0
                }
                if (this.type === 'cup') {
                    player.stats = player.stats.filter(element => element.cup)
                }
                player.stats.forEach(element => {
                    for (const key of Object.keys(stats)) {
                        stats[key] += key === 'fouls' ? foulsOf(element) : element[key]
                    }
                })
                return stats
            },
            getAverageStats(player) {
                const stats = {
                    points: 0,
                    rebounds: 0,
                    assists: 0,
                    steals: 0,
                    turnovers: 0,
                    blockedShots: 0,
                    twoPointsMade: 0,
                    twoPointAttempts: 0,
                    threePointsMade: 0,
                    threePointAttempts: 0,
                    freeThrowsMade: 0,
                    freeThrowAttempts: 0,
                    offensiveRebounds: 0,
                    defensiveRebounds: 0,
                    personalFouls: 0,
                    fouls: 0,
                    pir: 0,
                    playTimeInSecs: 0
                }
                if (this.type === 'cup') {
                    player.stats = player.stats.filter(element => element.cup)
                }
                player.stats.forEach(element => {
                    for (const key of Object.keys(stats)) {
                        stats[key] += key === 'fouls' ? foulsOf(element) : element[key]
                    }
                })
                Object.keys(stats).forEach(element => {
                    if (player.stats.length) {
                        stats[element] = stats[element] / player.stats.length
                    } else {
                        stats[element] = 0
                    }
                })
                return stats
            },
            fetchMatchesByTeam(teamId = this.$route.params.id) {
                matchService.getMatchesByTeam(teamId, null, this.type, this.type !== 'cup' && this.selectedLeague ? this.selectedLeague.id : null).then(resp => {
                    this.matches = resp.data
                    this.getMatchesSorted(this.matches)
                }).catch(error => {
                    if (error.response.data.code === 'MATCH_NOT_FOUND') {
                        this.matches = []
                    }
                })
            },
            getMatchesSorted() {
                this.matches.sort(function(a, b) {
                    if (a.round < b.round) {
                        return 1
                    } else {
                        return -1
                    }
                })
            }
        }
    }
</script>

<style scoped>
.mobile-sticky-banner {
    position: sticky;
    top: 80px;
    z-index: 1;
}

.pc-sticky-banner {
    position: sticky;
    top: 60px;
    z-index: 1;
}

</style>
